import { LEGACY_ANCHORS } from '@hbac/contract';
import { Suspense, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { About } from './sections/About';
import { Activities } from './sections/Activities';
import { Hero } from './sections/Hero';
import { LatestNews } from './sections/LatestNews';
import { SITE_TITLE, useDocumentTitle } from '../hooks/use-document-title';
import { lazyOptional } from '../lib/lazy-optional';

// HBAAC on the home page (Plan AO) comes with its own code and font, fetched
// right after the page, so the first download does not grow.
const HbaacSection = lazyOptional(() => import('./sections/HbaacSection'));

/**
 * The club landing page: one page, navigated by anchors.
 *
 * Multi-page was the first plan, but the closest comparable site
 * (banvannghethethao) uses a single page with anchor navigation, and at club
 * scale that is the right amount of structure — a visitor scrolls once rather
 * than hunting through a seven-item menu.
 */
export function LandingPage() {
  useDocumentTitle(SITE_TITLE);
  const { hash } = useLocation();
  const navigate = useNavigate();

  // Section anchors were renamed (#tin-tuc → #news, …). A fragment never
  // reaches the server, so old links are mapped here. Sections that render
  // after their content loads may not exist yet, hence the retry.
  useEffect(() => {
    const id = decodeURIComponent(hash.slice(1));
    const target = LEGACY_ANCHORS[id] ?? (id || null);
    if (!target) return;
    // Recruitment moved to a page of its own (27/09/2026).
    if (target === 'join') return void navigate('/join', { replace: true });
    if (target !== id) navigate({ hash: target }, { replace: true });
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(target);
      if (el || ++tries > 20) {
        clearInterval(timer);
        el?.scrollIntoView();
      }
    }, 100);
    return () => clearInterval(timer);
  }, [hash, navigate]);

  return (
    <>
      <Hero />
      <About />
      <Activities />
      <Suspense fallback={<div aria-hidden className="min-h-[640px] bg-navy-900" />}>
        <HbaacSection />
      </Suspense>
      <LatestNews />
    </>
  );
}
